/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import qs from 'query-string';
import { CommonLoading } from 'react-loadingg';
import {
  datePickerToStringFormat,
  handleDownloadTemplate,
  arrayToStringParser,
  stringToArrayParser,
} from 'helpers';
import api from 'api/server';
import Layout from 'components/layout';
import DetailModal from 'components/pages/FTP/Accepted/DetailModal/Modal';
import Sidebar from 'components/pages/FTP/Accepted/Sidebar/Sidebar';
import {
  Table, Button, Icon,
} from 'components/common';
import tableConfig from 'components/config/FTP/Accepted/tableConfig';
import { buttonStyles, containerStyles } from 'components/pages/FTP/Accepted/Accepted.styles';

const Accepted = () => {
  const history = useHistory();
  const queryString = history.location.search || '';
  const queryObject = qs.parse(queryString) || {};
  const modObj = {
    ...queryObject,
    follow_up_by: typeof (queryObject.follow_up_by) === 'string' ? [queryObject.follow_up_by] : queryObject.follow_up_by,
    payment_types: typeof (queryObject.payment_types) === 'string' ? [queryObject.payment_types] : queryObject.payment_types,
    payment_statuses: typeof (queryObject.payment_statuses) === 'string' ? [queryObject.payment_statuses] : queryObject.payment_statuses,
    follow_up_statuses: typeof (queryObject.follow_up_statuses) === 'string' ? [queryObject.follow_up_statuses] : queryObject.follow_up_statuses,
  };
  const [loading, setLoading] = useState(true);
  const [isModalDetailOpen, setModalDetail] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [callRange, setCallRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [callDate, callEndDate] = callRange;
  const [filterCondition, setFilterCondition] = useState({
    program: '',
    appliedBatch: '',
    enrolledBatch: '',
    paymentType: [],
    paymentStatuses: [],
    followUpStatus: [],
    followUpBy: [],
    search: '',
  });
  const [dataAccepted, setDataAccepted] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isCsvDownloading, setCsvDownloading] = useState(false);

  const csvHeaders = [
    { label: 'Applicant ID', key: 'applicant_id' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Enrolled Batch', key: 'enrolled_batch' },
    { label: 'Applied Batch', key: 'applied_batch' },
    { label: 'Payment Type', key: 'payment_type' },
    { label: 'Payment Status', key: 'payment_status' },
    { label: 'Payment Due Date', key: 'payment_due_date' },
  ];

  async function fetchData() {
    setLoading(true);
    try {
      const { data } = await api.getAllAccepted(modObj);
      const response = data.data;
      setDataAccepted(response.results);
      setPagination({
        current: response.current_page,
        total: response.last_page,
        totalData: response.total,
      });
    } catch (error) {
      if (error.response.status === 401) {
        Cookies.remove('access_token');
        history.replace('/sign-in');
      }
    } finally {
      setLoading(false);
    }
  }

  async function fetchDataCSV() {
    setCsvDownloading(true);
    try {
      const { data } = await api.getAllAccepted({ ...modObj, limit: 20000 });
      handleDownloadTemplate(data.data.results, csvHeaders, 'accepted_export');
    } catch (error) {
      console.log(error, 'error fetch data csv accepted');
    } finally {
      setCsvDownloading(false);
    }
  }

  const handleOpenAcceptedModal = (d) => {
    setModalDetail(true);
    setSelectedData(d);
  };

  useEffect(() => {
    fetchData();
    setFilterCondition(() => ({
      program: queryObject.program,
      followUpBy: stringToArrayParser(queryObject.follow_up_by),
      enrolledBatch: queryObject.enrolled_batch ? { label: queryObject.enrolled_batch, value: queryObject.enrolled_batch } : '',
      appliedBatch: queryObject.applied_batch ? { label: queryObject.applied_batch, value: queryObject.applied_batch } : '',
      paymentType: stringToArrayParser(queryObject.payment_types),
      followUpStatus: stringToArrayParser(queryObject.follow_up_statuses),
      paymentStatuses: stringToArrayParser(queryObject.payment_statuses),
      search: queryObject.search,
    }));
    setDateRange(
      [queryObject.last_updated_start_date ? new Date(queryObject.last_updated_start_date) : null,
        queryObject.last_updated_end_date ? new Date(queryObject.last_updated_end_date) : null,
      ],
    );
    setCallRange(
      [queryObject.call_start_date ? new Date(queryObject.call_start_date) : null,
        queryObject.call_end_date ? new Date(queryObject.call_end_date) : null,
      ],
    );
  }, [queryString]);

  const applicantData = dataAccepted.map((d) => ({
    key: d.id,
    page: d.applicant_id,
    subpage: 'personal-data',
    id: d.applicant_id,
    registered_batch: d.applied_batch,
    enrolled_batch: d.enrolled_batch,
    name: d.name,
    payment_option: d.payment_type,
    payment_status: d.payment_status,
    payment_due: d.next_payment_due_date,
    action: (
      <div>
        <Button
          styles={buttonStyles}
          type="button"
          onClick={() => handleOpenAcceptedModal(d)}
        >
          <Icon name="chevron-right" />
        </Button>
      </div>
    ),
  }));

  const handleChange = (e) => {
    setFilterCondition({
      ...filterCondition,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelect2Change = (e, name) => {
    setFilterCondition({
      ...filterCondition,
      [name]: e,
    });
  };

  const handleResetFilter = () => {
    setFilterCondition({
      program: '',
      appliedBatch: '',
      enrolledBatch: '',
      paymentType: [],
      paymentStatuses: [],
      followUpStatus: [],
      followUpBy: [],
      search: '',
    });
    setDateRange([null, null]);
    setCallRange([null, null]);
    // Clearing the URL lets the [queryString] effect refetch and re-sync the filters.
    history.push({
      pathname: history.location.pathname,
      search: '',
    });
  };

  const handleSubmitFilter = (e) => {
    e.preventDefault();
    const queryFilter = {
      last_updated_start_date: dateRange[0] ? datePickerToStringFormat(JSON.stringify(dateRange[0])) : '',
      call_start_date: callRange[0] ? datePickerToStringFormat(JSON.stringify(callRange[0])) : '',
      last_updated_end_date: dateRange[1] ? datePickerToStringFormat(JSON.stringify(dateRange[1])) : '',
      call_end_date: callRange[1] ? datePickerToStringFormat(JSON.stringify(callRange[1])) : '',
      applied_batch: filterCondition.appliedBatch?.value,
      enrolled_batch: filterCondition.enrolledBatch?.value,
      payment_types: arrayToStringParser(filterCondition.paymentType),
      payment_statuses: arrayToStringParser(filterCondition.paymentStatuses),
      follow_up_statuses: arrayToStringParser(filterCondition.followUpStatus),
      follow_up_by: arrayToStringParser(filterCondition.followUpBy),
      program: filterCondition.program,
      search: filterCondition.search,
    };
    const queryParam = qs.stringify(queryFilter, { skipNull: true, skipEmptyString: true });
    history.push({
      pathname: history.location.pathname,
      search: queryParam,
    });
  };

  return (
    <Layout
      containerStyles={isModalDetailOpen ? 'z-index: -1;' : ''}
      sidebar={(
        <Sidebar
          search={{
            name: 'search',
            value: filterCondition.search,
            onChange: handleChange,
            onSubmit: handleSubmitFilter,
            placeholder: 'search by name / email',
          }}
          startDate={startDate}
          callDate={callDate}
          endDate={endDate}
          callEndDate={callEndDate}
          setDateRange={setDateRange}
          setCallRange={setCallRange}
          filterCondition={filterCondition}
          setFilterCondition={setFilterCondition}
          handleChange={handleChange}
          handleSelect2Change={handleSelect2Change}
          handleSubmit={handleSubmitFilter}
          handleReset={handleResetFilter}
          handleDownloadCSV={fetchDataCSV}
          isCsvDownloading={isCsvDownloading}
        />
      )}
    >
      <div css={containerStyles}>
        <span>
          total record: &nbsp;
          {pagination.totalData}
        </span>
      </div>
      {
        loading ? <CommonLoading /> : (
          <Table
            containerStyles={css`height: 80vh;`}
            tableConfig={tableConfig}
            history={history}
            pagination={pagination}
            setPagination={setPagination}
            data={applicantData}
          />
        )
      }
      <DetailModal
        isModalOpen={isModalDetailOpen}
        setModal={setModalDetail}
        data={selectedData}
      />
    </Layout>
  );
};
export default Accepted;
